import { submitGuard } from './alpine-submit-guard.js';

/**
 * Alpine component for the suspension-appeal form (auth/appeal.twig).
 *
 * Tracks the character count for the appeal message and exposes min/max
 * length thresholds so the template can show "N characters remaining" /
 * "N / max" hints. Configuration arrives via a data-config JSON attribute
 * on the host element so this module stays free of Twig interpolation.
 *
 * Usage in Twig:
 *
 *   <form x-data="appealForm"
 *         data-config='{
 *             "messageText": {{ message|json_encode(15)|raw }},
 *             "minLength": {{ min_length }},
 *             "maxLength": {{ max_length }},
 *             "geoFailure": {{ geo_failure|default('')|json_encode(15)|raw }}
 *         }'>
 *     ...
 *   </form>
 */

/**
 * The two failure reasons the server accepts in geo_failure. They mirror
 * `AppealUserFormService::GEO_FAILURE_REASONS`, and the permission-denied vs
 * device-failure split matches the signup flow's mapping in
 * modules/signup/geolocation/browserGeo.js.
 */
const GEO_FAILURE_REASONS = ['blocked', 'unavailable'];

/**
 * Map a GeolocationPositionError onto a geo_failure reason.
 * code 1 PERMISSION_DENIED -> blocked (the prompt was denied, by the user or
 * by a standing site permission). code 2 POSITION_UNAVAILABLE and code 3
 * TIMEOUT -> unavailable (the device produced no fix).
 */
function mapGeolocationError(error) {
    return (error && error.code === 1) ? 'blocked' : 'unavailable';
}

export function appealForm() {
    return {
        ...submitGuard(),

        messageText: '',
        minLength: 0,
        maxLength: 0,

        // Structured submissions require a share or refuse choice. Shared
        // coordinates are coarsened to two decimals before they populate the
        // hidden fields, so a precise fix never leaves the device.
        //
        // geoStatus doubles as the submitted failure reason: 'blocked' and
        // 'unavailable' are the two values the server accepts in geo_failure,
        // so a share attempt that produced no fix records *why* rather than
        // being funnelled into the refusal path.
        geoChoice: '',
        geoChoiceRequired: '',
        geoStatus: 'idle',
        geoLat: '',
        geoLng: '',
        geoAccuracy: '',
        geoRequestId: 0,

        init() {
            const raw = this.$root?.dataset?.config;
            if (raw) {
                try {
                    const config = JSON.parse(raw);
                    if (typeof config.messageText === 'string') {
                        this.messageText = config.messageText;
                    }
                    if (Number.isFinite(config.minLength)) {
                        this.minLength = config.minLength;
                    }
                    if (Number.isFinite(config.maxLength)) {
                        this.maxLength = config.maxLength;
                    }
                    if (config.geoChoice === 'share' || config.geoChoice === 'refuse') {
                        this.geoChoice = config.geoChoice;
                    }
                    if (typeof config.geoLat === 'string' || Number.isFinite(config.geoLat)) {
                        this.geoLat = String(config.geoLat);
                    }
                    if (typeof config.geoLng === 'string' || Number.isFinite(config.geoLng)) {
                        this.geoLng = String(config.geoLng);
                    }
                    if (typeof config.geoAccuracy === 'string' || Number.isFinite(config.geoAccuracy)) {
                        this.geoAccuracy = String(config.geoAccuracy);
                    }
                    if (typeof config.geoChoiceRequired === 'string') {
                        this.geoChoiceRequired = config.geoChoiceRequired;
                    }
                    if (this.geoChoice === 'share') {
                        if (this.geoLat !== '' && this.geoLng !== '') {
                            // Coordinates settle it. A reason echoed back
                            // alongside them is stale and must not suppress the
                            // shared confirmation.
                            this.geoStatus = 'shared';
                        } else if (GEO_FAILURE_REASONS.includes(config.geoFailure)) {
                            this.geoStatus = config.geoFailure;
                        }
                        // A redisplayed share choice with neither coordinates
                        // nor a reason stays 'idle', which keeps the choice
                        // unanswered. Inventing a reason here would report a
                        // device failure nobody observed. Clicking the share
                        // option runs the request (retryShareLocation), and
                        // refusal stays available.
                    }
                } catch (_e) {
                    // Bad JSON in data-config: leave defaults; the field still
                    // works, the counter just won't enforce any thresholds.
                }
            }

            // After a failed server-side validation pass, the template marks
            // the first errored input with data-autofocus-error so we can
            // restore focus there.
            const target = this.$root.querySelector('[data-autofocus-error]');
            if (target instanceof HTMLElement) {
                target.focus({ preventScroll: false });
            }

            this.$nextTick(() => this.syncGeoChoiceValidity());

            this.initSubmitGuard();
        },

        get charCount() {
            return (this.messageText || '').length;
        },

        /**
         * Request browser geolocation and coarsen it to two decimals (~1 km)
         * before writing the hidden fields. When no fix arrives, the share
         * choice stays selected and submittable with the reason recorded in
         * geo_failure, so a device that cannot produce a fix is never stored
         * as a refusal.
         */
        shareLocation() {
            // A second tap while the first request is still in flight would
            // fire a duplicate getCurrentPosition. Ignore it until the prompt
            // resolves (shared / blocked / unavailable).
            if (this.geoStatus === 'loading') {
                return;
            }

            this.geoChoice = 'share';
            this.geoLat = '';
            this.geoLng = '';
            this.geoAccuracy = '';

            if (!('geolocation' in navigator)) {
                this.geoStatus = 'unavailable';
                this.syncGeoChoiceValidity();
                return;
            }

            this.geoStatus = 'loading';
            this.syncGeoChoiceValidity();
            const requestId = ++this.geoRequestId;
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (requestId !== this.geoRequestId || this.geoChoice !== 'share') {
                        return;
                    }
                    const coarsen = (value) => (Math.round(value * 100) / 100).toFixed(2);
                    this.geoLat = coarsen(position.coords.latitude);
                    this.geoLng = coarsen(position.coords.longitude);
                    const accuracy = position.coords.accuracy;
                    this.geoAccuracy = Number.isFinite(accuracy) ? String(Math.round(accuracy)) : '';
                    this.geoStatus = 'shared';
                    this.syncGeoChoiceValidity();
                },
                (error) => {
                    if (requestId !== this.geoRequestId || this.geoChoice !== 'share') {
                        return;
                    }
                    this.geoStatus = mapGeolocationError(error);
                    this.syncGeoChoiceValidity();
                },
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
            );
        },

        /**
         * Re-run the location request when the share option is clicked while
         * already selected. Re-selecting a checked radio fires no change
         * event, so without this a share choice left unanswered or reporting a
         * failure has no way back to a successful attempt. The click lands
         * before x-model applies a new selection, so on a fresh selection
         * geoChoice still holds the previous value here and the request is
         * left to the change handler.
         */
        retryShareLocation() {
            if (this.geoChoice !== 'share') {
                return;
            }
            // A captured fix is not re-requested. Clearing it for an attempt
            // that then fails would lose the location already shared.
            if (this.geoStatus === 'shared' || this.geoStatus === 'loading') {
                return;
            }
            this.shareLocation();
        },

        refuseLocation() {
            this.geoRequestId += 1;
            this.geoChoice = 'refuse';
            this.geoStatus = 'idle';
            this.geoLat = '';
            this.geoLng = '';
            this.geoAccuracy = '';
            this.syncGeoChoiceValidity();
        },

        /**
         * The reason submitted in geo_failure. Empty unless the share choice
         * is selected and the attempt produced no fix, which is the only
         * combination the server accepts a reason for.
         */
        get geoFailure() {
            if (this.geoChoice !== 'share' || !GEO_FAILURE_REASONS.includes(this.geoStatus)) {
                return '';
            }
            return this.geoStatus;
        },

        syncGeoChoiceValidity() {
            const shareReady = this.geoChoice === 'share'
                && this.geoStatus === 'shared'
                && this.geoLat !== ''
                && this.geoLng !== '';
            // A share attempt that failed is still a complete answer: the
            // reason travels in geo_failure. Only 'idle' and 'loading' leave
            // the choice unanswered.
            const valid = this.geoChoice === 'refuse' || shareReady || this.geoFailure !== '';
            const shareControl = this.$refs.geoShareChoice;
            if (shareControl instanceof HTMLInputElement) {
                shareControl.setCustomValidity(valid ? '' : this.geoChoiceRequired);
            }
        },

        /**
         * The submit latch is bound to the button's click, deliberately NOT to
         * a form-level Alpine submit directive. Captcha::printCaptcha() refuses
         * to bind its reCAPTCHA v3 token-refresh submit handler on any form
         * that declares @submit / x-on:submit, which would leave #googleResponse
         * empty and fail every non-China appeal POST server-side.
         */
        prepareSubmit() {
            this.syncGeoChoiceValidity();

            return true;
        },
    };
}
